/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Fade,
    Flex,
    Stack,
    Text,
    Button,
    VStack,
    HStack,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AddIcon, MinusIcon} from '@chakra-ui/icons'

// Project Components
import ItemVariantProvider from '@salesforce/retail-react-app/app/components/item-variant'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'

// Import your custom styles - adjust path as needed
import {
    calloutBoxStyle,
    containerStyle,
    errorMessageStyle,
    flexStyle,
    hStackStyle,
    iconStyle,
    imageStyle,
    inputStyle,
    minusButtonStyle,
    plusButtonStyle,
    priceBaseStyle,
    priceStyle,
    productNameStyle,
    shipToHomeStyle,
    surchargeStyle,
    tagIconStyle
} from './index.style'
import DisplayPrice from '../display-price'
import {filterImageGroups, getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import CartItemVariantAttributes from '../item-variant/item-attributes'
import Link from '@salesforce/retail-react-app/app/components/link'
import {pxToRem} from '../../utills/pxToRem'
import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import CloudinaryPlpImage from '../../cloudinary/cloudinary-plp-images'
import {variantValues} from '../item-variant/index.style'
import useCustomFetch from '../../hooks/useStoreDetails'
import {useAccessToken, useProduct} from '@salesforce/commerce-sdk-react'
import {useGlobalError} from '../../pages/cart/util/global-error-context'
import PromotionsSection from '../promotions-section'

const QuantityPickerSection = ({
    quantity,
    setSelectedQuantity,
    minOrderQuantity = 1,
    stockLevel,
    setQuantity,
    stepQuantity = 1,
    isVisible = true,
    onQuantityChange,
    product,
    onCheckStock,
    disableIncrease = false
}) => {
    const maxAllowed = Math.min(stockLevel, 10)
    const [isLoading, setIsLoading] = useState(false)

    const dec = async () => {
        try {
            setIsLoading(true)

            let currentStockLevel = stockLevel

            if (onCheckStock && product) {
                currentStockLevel = await onCheckStock(product)
            }

            if (quantity > currentStockLevel) {
                const adjustedQuantity = Math.max(currentStockLevel, minOrderQuantity)
                setQuantity(adjustedQuantity)
                if (onQuantityChange) {
                    onQuantityChange(adjustedQuantity)
                }
            } else {
                const newQuantity =
                    quantity - stepQuantity >= minOrderQuantity ? quantity - stepQuantity : quantity
                setQuantity(newQuantity)
                if (onQuantityChange) {
                    onQuantityChange(newQuantity)
                }
            }
        } catch (error) {
            console.error('Error checking stock level:', error)
            const newQuantity =
                quantity - stepQuantity >= minOrderQuantity ? quantity - stepQuantity : quantity
            setQuantity(newQuantity)
            if (onQuantityChange) {
                onQuantityChange(newQuantity)
            }
        } finally {
            setIsLoading(false)
        }
    }

    const inc = () => {
        const newQuantity =
            quantity + stepQuantity <= maxAllowed ? quantity + stepQuantity : quantity
        setQuantity(newQuantity)
        if (onQuantityChange) {
            onQuantityChange(newQuantity)
        }
    }

    const plusDisabled = quantity + stepQuantity > maxAllowed || disableIncrease
    const minusDisabled = quantity - stepQuantity < minOrderQuantity

    if (setSelectedQuantity) {
        setSelectedQuantity(quantity)
    }

    return (
        isVisible && (
            <Flex sx={containerStyle}>
                <Button
                    sx={minusButtonStyle}
                    onClick={dec}
                    isDisabled={minusDisabled || isLoading}
                    isLoading={isLoading}
                >
                    <MinusIcon sx={iconStyle} />
                </Button>
                <Text sx={inputStyle} as="span">
                    {quantity}
                </Text>
                <Button sx={plusButtonStyle} onClick={inc} isDisabled={plusDisabled}>
                    <AddIcon sx={iconStyle} />
                </Button>
            </Flex>
        )
    )
}

QuantityPickerSection.propTypes = {
    quantity: PropTypes.number.isRequired,
    minOrderQuantity: PropTypes.number,
    stockLevel: PropTypes.number,
    setQuantity: PropTypes.func.isRequired,
    stepQuantity: PropTypes.number,
    isVisible: PropTypes.bool,
    setSelectedQuantity: PropTypes.func,
    onQuantityChange: PropTypes.func,
    product: PropTypes.object,
    onCheckStock: PropTypes.func,
    disableIncrease: PropTypes.bool
}

const getInventoryErrorType = (quantity, stockLevel) => {
    if (stockLevel <= 0) {
        return 'OUT_OF_STOCK'
    }
    if (quantity > stockLevel) {
        return 'QUANTITY_EXCEEDED'
    }
    return null
}

/**
 * Component representing a product item usually in a list with details about the product - name, variant, pricing, etc.
 * @param {Object} product Product to be represented in the list item.
 * @param {node} primaryAction Child component representing the most prominent action to be performed by the user.
 * @param {node} secondaryActions Child component representing the other actions relevant to the product to be performed by the user.
 * @param {func} onItemQuantityChange callback function to be invoked whenever item quantity changes.
 * @param {boolean} showLoading Renders a loading spinner with overlay if set to true.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const ProductItem = ({
    product,
    primaryAction,
    secondaryActions,
    onItemQuantityChange = noop,
    showLoading = false,
    customStyles = {},
    isCart = true,
    isAccountPage = false,
    isOrderConfirmation = false,
    disableQuantityIncrease = false
}) => {
    const isProductPartOfSet = !!product?.type?.set || false
    const isProductPartOfBundle = !!product?.type?.bundle || false
    const globalErrorContext = useGlobalError()
    const addError = globalErrorContext?.addError || (() => {})
    const removeError = globalErrorContext?.removeError || (() => {})

    const {data: tempProd} = useProduct({
        parameters: {
            id: product?.originalProductId,
            perPricebook: true,
            expand: [
                'availability',
                'promotions',
                'options',
                'images',
                'prices',
                'variations',
                'set_products',
                'bundled_products'
            ],
            allImages: true,
            c_isPDPRequest: true,
            c_isPickUpInStore: product?.showInStorePickup,
            inventoryIds: product?.c_storeId ? `inventory_store_${product?.c_storeId}` : null
        }
    })

    let {stepQuantity, showInventoryMessage, inventoryMessage, stockLevel, setQuantity} =
        useDerivedProduct(tempProd, isProductPartOfSet, isProductPartOfBundle)

    const {quantity} = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

    const [latestStockLevel, setLatestStockLevel] = useState(() => {
        if (tempProd?.inventories && tempProd.inventories.length > 0) {
            return tempProd.inventories[0].stockLevel || 0
        } else if (tempProd?.inventory) {
            return tempProd.inventory.stockLevel || 0
        } else if (stockLevel !== undefined) {
            return stockLevel
        }
        return 0
    })

    useEffect(() => {
        if (tempProd?.inventories && tempProd.inventories.length > 0) {
            const newStockLevel = tempProd.inventories[0].stockLevel || 0
            setLatestStockLevel(newStockLevel)
        } else if (tempProd?.inventory) {
            const newStockLevel = tempProd.inventory.stockLevel || 0
            setLatestStockLevel(newStockLevel)
        } else if (tempProd?.c_availableCount) {
            const newStockLevel = Number(tempProd.c_availableCount) || 0
            setLatestStockLevel(newStockLevel)
        } else if (stockLevel !== undefined) {
            setLatestStockLevel(stockLevel)
        }
    }, [tempProd, stockLevel])

    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()

    const isGiftCertificate = product?.isGiftCertificate
    const [stockDataLoaded, setStockDataLoaded] = useState(false)

    useEffect(() => {
        if (
            tempProd &&
            (tempProd.inventories ||
                tempProd.inventory ||
                tempProd.c_availableCount ||
                stockLevel !== undefined)
        ) {
            setStockDataLoaded(true)
        }
    }, [tempProd, stockLevel])

    const inventoryErrorType = stockDataLoaded
        ? getInventoryErrorType(quantity || 0, latestStockLevel || 0)
        : null

    const handleCustomQuantityChange = (newQuantity) => {
        if (newQuantity >= 0) {
            onItemQuantityChange(newQuantity).then((isValidChange) => {
                if (isValidChange) {
                    setQuantity(newQuantity)
                }
            })
        }
    }

    const {data: freshProductData, refetch: refetchProduct} = useProduct(
        {
            parameters: {
                id: product?.originalProductId,
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'options',
                    'images',
                    'prices',
                    'variations',
                    'set_products',
                    'bundled_products'
                ],
                allImages: true,
                c_isPDPRequest: true,
                c_isPickUpInStore: product?.showInStorePickup,
                inventoryIds: product?.c_storeId ? `inventory_store_${product?.c_storeId}` : null
            }
        },
        {
            enabled: false
        }
    )

    const handleCheckStock = async (productToCheck) => {
        try {
            const result = await refetchProduct()

            if (result.data) {
                let currentStock = latestStockLevel || stockLevel || 0

                if (result.data.inventories && result.data.inventories.length > 0) {
                    currentStock = result.data.inventories[0].stockLevel || 0
                } else if (result.data.inventory) {
                    currentStock = result.data.inventory.stockLevel || 0
                } else if (result.data.c_availableCount) {
                    currentStock = Number(result.data.c_availableCount) || 0
                }

                setLatestStockLevel(currentStock)
                return currentStock
            } else {
                return latestStockLevel || 0
            }
        } catch (error) {
            console.error('Error fetching stock level:', error)
            return latestStockLevel || stockLevel || 0
        }
    }

    const priceData = product && getPriceData(product, {quantity})

    const newPriceData = {
        ...priceData,
        currentPrice: product?.finalPrice || priceData?.currentPrice,
        listPrice: product?.basePrice || priceData?.listPrice,
        isOnSale:
            (product?.finalPrice || priceData?.currentPrice) <
            (product?.basePrice || priceData?.listPrice)
    }

    const isMasterVariant = !!product?.variants
    const selectableAttributeValue =
        isMasterVariant && !!product?.representedProduct
            ? product?.variants?.find(
                  (variant) => variant.productId == product.representedProduct.id
              )?.variationValues?.[selectableAttributeId]
            : undefined
    const selectableAttributeId = PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID
    const imageViewType = PRODUCT_TILE_IMAGE_VIEW_TYPE

    const image = useMemo(() => {
        const hasSelectableAttribute = product?.variationAttributes?.find(
            ({id}) => id === selectableAttributeId
        )

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}
        const filteredImageGroups = filterImageGroups(product?.imageGroups || [], {
            viewType: imageViewType,
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        return filteredImageGroups?.[0]?.images[0]
    }, [product, selectableAttributeId, selectableAttributeValue, imageViewType])

    const {token} = useAccessToken()
    const {response} = useCustomFetch(token, product?.c_storeId)
    const shippingState = product?.shippingState

    const shouldShowError =
        !showLoading && tempProd && (product?.isRestricted || inventoryErrorType)

    useEffect(() => {
        if (shouldShowError && !isGiftCertificate && inventoryErrorType) {
            addError(product.itemId)
        } else {
            removeError(product.itemId)
        }

        return () => {
            removeError(product.itemId)
        }
    }, [shouldShowError, inventoryErrorType, isGiftCertificate, product.itemId])

    const transformProductUrl = (urlPath) => {
        if (!urlPath) {
            return ''
        }
        const url = new URL(urlPath).pathname
        const segments = url.split('/')
        const filename = segments.pop().replace('.html', '')
        const parts = filename.split('_')
        const colorCode = parts.slice(1).find((part) => /^\d+$/.test(part)) || ''
        const newPath = `${segments.join('/')}/${parts[0]}.html`
        return colorCode ? `${newPath}?color=${colorCode}` : newPath
    }

    const transformedUrl = transformProductUrl(product?.slugUrl || tempProd?.slugUrl)

    return (
        <>
            <Box className="inner-container" display={{base: 'none', md: 'block'}}>
                <Box
                    position="relative"
                    height="fit-content"
                    display="flex"
                    flexDirection="column"
                    data-testid={`sf-cart-item-${
                        product.productId ? product.productId : product.id
                    }`}
                >
                    <ItemVariantProvider variant={product}>
                        {showLoading && <LoadingSpinner />}
                        <Flex
                            width="full"
                            alignItems="stretch"
                            backgroundColor="white"
                            minHeight="200px"
                            height="fit-content"
                        >
                            {isGiftCertificate ? (
                                <Box
                                    className="image-container"
                                    sx={{
                                        ...(customStyles.imageStyle || imageStyle),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img
                                        src={product?.imageUrl}
                                        alt="Gift Certificate"
                                        title={product?.name}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    className="image-container"
                                    sx={customStyles.imageStyle || imageStyle}
                                >
                                    <CloudinaryPlpImage
                                        cloudinaryImage={
                                            product?.c_cloudinary ? product.c_cloudinary : null
                                        }
                                        image={image}
                                        title={product?.name}
                                    />
                                </Box>
                            )}
                            <Stack
                                flex={1}
                                spacing={0}
                                sx={{
                                    m: `${pxToRem(8)} ${pxToRem(10)} ${pxToRem(8)} ${pxToRem(15)}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Box sx={{flex: '1 1 auto'}}>
                                    <Box sx={{mb: pxToRem(14), lineHeight: 'normal'}}>
                                        {isGiftCertificate ? (
                                            <Text sx={productNameStyle}>{product.name}</Text>
                                        ) : (
                                            <Link to={transformedUrl} sx={productNameStyle}>
                                                {product.name}
                                            </Link>
                                        )}
                                        {product?.c_freightSurcharge && (
                                            <Text sx={surchargeStyle}>
                                                {product.c_freightSurcharge}
                                            </Text>
                                        )}
                                    </Box>
                                    <Box>
                                        {isGiftCertificate ? (
                                            <Stack spacing="10px">
                                                {product.price && (
                                                    <Box>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_amount',
                                                                    defaultMessage:
                                                                        'Amount: ${price}'
                                                                },
                                                                {price: product?.price}
                                                            )}
                                                        </Text>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_to',
                                                                    defaultMessage:
                                                                        'To: {recipientName}'
                                                                },
                                                                {
                                                                    recipientName:
                                                                        product?.recipientName
                                                                }
                                                            )}
                                                        </Text>
                                                        <Text sx={variantValues}>
                                                            {intl.formatMessage(
                                                                {
                                                                    id: 'cart_from',
                                                                    defaultMessage:
                                                                        'From: {senderName}'
                                                                },
                                                                {
                                                                    senderName: product?.senderName
                                                                }
                                                            )}
                                                        </Text>
                                                    </Box>
                                                )}
                                                <Text as="span" sx={shipToHomeStyle}>
                                                    {intl.formatMessage(
                                                        {
                                                            id: 'cart_email_delivery',
                                                            defaultMessage:
                                                                'Email Delivery: {recipientEmail}'
                                                        },
                                                        {
                                                            recipientEmail: product?.recipientEmail
                                                        }
                                                    )}
                                                </Text>
                                            </Stack>
                                        ) : (
                                            <CartItemVariantAttributes
                                                product={product}
                                                includeQuantity={!isCart}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{mb: pxToRem(16)}}>
                                        {product?.c_storeId && product?.showInStorePickup ? (
                                            <Text
                                                as="span"
                                                sx={{...shipToHomeStyle, mb: pxToRem(9)}}
                                            >
                                                {intl.formatMessage({
                                                    id: 'product_list.store.pickupinstore',
                                                    defaultMessage: 'Pick Up in Store'
                                                })}
                                            </Text>
                                        ) : (
                                            product?.showShipToHome && (
                                                <Text sx={{...shipToHomeStyle, mb: pxToRem(13)}}>
                                                    {intl.formatMessage({
                                                        id: 'product_list.store.shiptome',
                                                        defaultMessage: 'Ship to Me'
                                                    })}
                                                </Text>
                                            )
                                        )}
                                        {product?.c_storeId && product?.showInStorePickup && (
                                            <Text sx={{...shipToHomeStyle, mb: pxToRem(9)}}>
                                                {`${response?.data?.[0].address1} ${response?.data?.[0].city}, ${response?.data?.[0].stateCode} ${response?.data?.[0].postalCode}`}
                                            </Text>
                                        )}
                                    </Box>
                                </Box>
                                <Box
                                    sx={{flex: '0 0 auto'}}
                                    width={isAccountPage ? '100%' : 'auto'}
                                >
                                    <Flex
                                        direction="row"
                                        align="end"
                                        justify="space-between"
                                        width={isAccountPage ? '100%' : 'auto'}
                                    >
                                        <Flex
                                            direction="column"
                                            gap={0}
                                            width={isAccountPage ? '100%' : 'auto'}
                                        >
                                            <Box
                                                sx={{
                                                    maxW: {
                                                        xl: pxToRem(300)
                                                    },
                                                    mb: pxToRem(10),
                                                    display: 'block'
                                                }}
                                            >
                                                <DisplayPrice
                                                    currency={activeCurrency}
                                                    priceData={newPriceData}
                                                    currentPriceProps={
                                                        newPriceData.isOnSale
                                                            ? {...priceBaseStyle, ...priceStyle}
                                                            : {...priceBaseStyle}
                                                    }
                                                    listPriceProps={priceBaseStyle}
                                                />

                                                <PromotionsSection
                                                    promos={product?.productPromotions}
                                                />
                                            </Box>
                                            <Flex
                                                align="end"
                                                justify="space-between"
                                                flexWrap="no-wrap"
                                            >
                                                <Box>{secondaryActions}</Box>
                                                {isAccountPage && <Box>{primaryAction}</Box>}
                                            </Flex>
                                        </Flex>
                                        {(latestStockLevel || 0) > 0 && isCart && (
                                            <Box
                                                sx={{
                                                    alignSelf: 'flex-end',
                                                    mr: pxToRem(-10),
                                                    mb: pxToRem(2)
                                                }}
                                            >
                                                <QuantityPickerSection
                                                    quantity={quantity}
                                                    setQuantity={setQuantity}
                                                    stepQuantity={stepQuantity}
                                                    stockLevel={latestStockLevel}
                                                    minOrderQuantity={1}
                                                    isVisible={true}
                                                    onQuantityChange={handleCustomQuantityChange}
                                                    product={product}
                                                    onCheckStock={handleCheckStock}
                                                    disableIncrease={disableQuantityIncrease}
                                                />
                                            </Box>
                                        )}
                                    </Flex>
                                </Box>
                            </Stack>
                        </Flex>
                        {!isGiftCertificate && shouldShowError && (
                            <Stack spacing={1.5} alignItems="flex-start" sx={{mt: pxToRem(4)}}>
                                {product?.isRestricted && !product?.showInStorePickup && (
                                    <Flex flexDirection="row" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'cart_error.shipment_change',
                                                    defaultMessage: `Item can't be shipped to {shippingState} please remove or change shipping address.`
                                                },
                                                {shippingState}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'OUT_OF_STOCK' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage({
                                                id: 'cart_error.out_of_stock',
                                                defaultMessage:
                                                    'This item is no longer available. Please remove this from the cart.'
                                            })}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'QUANTITY_EXCEEDED' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'cart_error.quantity_exceeded',
                                                    defaultMessage:
                                                        'The quantity selected is not currently available, please enter a quantity of {stockLevel} or less to proceed with this purchase.'
                                                },
                                                {stockLevel: latestStockLevel}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                            </Stack>
                        )}
                    </ItemVariantProvider>
                </Box>
            </Box>
            <Box display={{base: 'block', md: 'none'}} width={'100%'}>
                <Box
                    position="relative"
                    width="100%"
                    height="100%"
                    data-testid={`sf-cart-item-${
                        product.productId ? product.productId : product.id
                    }`}
                >
                    <ItemVariantProvider variant={product}>
                        {showLoading && <LoadingSpinner />}
                        <Flex direction="row" alignItems="flex-start">
                            {isGiftCertificate ? (
                                <img
                                    src={product.imageUrl}
                                    alt="Gift Certificate"
                                    title={product.name}
                                    style={{width: '127px', height: 'auto'}}
                                />
                            ) : (
                                <Box sx={customStyles.imageStyle || imageStyle}>
                                    <CloudinaryPlpImage
                                        cloudinaryImage={
                                            product?.c_cloudinary ? product.c_cloudinary : null
                                        }
                                        image={image}
                                        title={product.name}
                                    />
                                </Box>
                            )}
                            <Stack
                                flex={1}
                                spacing="0"
                                sx={{
                                    m: `${pxToRem(2)} ${pxToRem(0)} ${pxToRem(8)} ${pxToRem(10)}`,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: '127px',
                                    height: '100%'
                                }}
                            >
                                <Box
                                    sx={{
                                        mb: {base: pxToRem(2), md: pxToRem(5)},
                                        lineHeight: 'normal'
                                    }}
                                >
                                    {isGiftCertificate ? (
                                        <Text sx={productNameStyle}>{product.name}</Text>
                                    ) : (
                                        <Link to={transformedUrl} sx={productNameStyle}>
                                            {product.name}
                                        </Link>
                                    )}
                                    {product?.c_freightSurcharge && (
                                        <Text sx={surchargeStyle}>
                                            {product.c_freightSurcharge}
                                        </Text>
                                    )}
                                </Box>
                                <Box sx={{mb: pxToRem(5)}}>
                                    {isGiftCertificate ? (
                                        <Stack spacing="5px">
                                            {product.price && (
                                                <Box>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_amount',
                                                                defaultMessage: 'Amount: ${price}'
                                                            },
                                                            {price: product.price}
                                                        )}
                                                    </Text>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_to',
                                                                defaultMessage:
                                                                    'To: {recipientName}'
                                                            },
                                                            {
                                                                recipientName: product.recipientName
                                                            }
                                                        )}
                                                    </Text>
                                                    <Text sx={variantValues}>
                                                        {intl.formatMessage(
                                                            {
                                                                id: 'cart_from',
                                                                defaultMessage: 'From: {senderName}'
                                                            },
                                                            {senderName: product.senderName}
                                                        )}
                                                    </Text>
                                                </Box>
                                            )}
                                            <Text as="span" sx={shipToHomeStyle}>
                                                {intl.formatMessage(
                                                    {
                                                        id: 'cart_email_delivery',
                                                        defaultMessage:
                                                            'Email Delivery: {recipientEmail}'
                                                    },
                                                    {recipientEmail: product?.recipientEmail}
                                                )}
                                            </Text>
                                        </Stack>
                                    ) : (
                                        <CartItemVariantAttributes
                                            product={product}
                                            includeQuantity={!isCart}
                                            currency={product?.currency}
                                        />
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        mx: 0,
                                        my: 0,
                                        px: 0,
                                        py: 0,
                                        mb: pxToRem(5)
                                    }}
                                >
                                    {product?.showInStorePickup ? (
                                        <Text as="span" sx={shipToHomeStyle}>
                                            {intl.formatMessage({
                                                id: 'product_list.store.pickupinstore',
                                                defaultMessage: 'Pick Up in Store'
                                            })}
                                        </Text>
                                    ) : (
                                        product?.showShipToHome && (
                                            <Text as="span" sx={shipToHomeStyle}>
                                                {intl.formatMessage({
                                                    id: 'product_list.store.shiptome',
                                                    defaultMessage: 'Ship to Me'
                                                })}
                                            </Text>
                                        )
                                    )}
                                    {product?.c_storeId && product?.showInStorePickup && (
                                        <>
                                            <Text sx={{...shipToHomeStyle, m: 0}}>
                                                {response?.data?.[0].address1}
                                            </Text>
                                            <Text
                                                sx={{...shipToHomeStyle, m: 0}}
                                            >{`${response?.data?.[0].city}, ${response?.data?.[0].stateCode} ${response?.data?.[0].postalCode}`}</Text>
                                        </>
                                    )}
                                </Box>
                                <Box sx={{mb: pxToRem(5)}}>
                                    <DisplayPrice
                                        currency={activeCurrency}
                                        priceData={newPriceData}
                                        currentPriceProps={
                                            newPriceData.isOnSale
                                                ? {...priceBaseStyle, ...priceStyle}
                                                : {...priceBaseStyle}
                                        }
                                        listPriceProps={priceBaseStyle}
                                    />
                                </Box>
                            </Stack>
                        </Flex>
                        <Stack spacing="0" width={'100%'}>
                            <Box>
                                <PromotionsSection promos={product?.productPromotions} />
                            </Box>
                            <Flex
                                direction="row"
                                align="center"
                                justify="space-between"
                                flexWrap="wrap"
                            >
                                <Box>{secondaryActions}</Box>
                                {isAccountPage && <Box width="100%">{primaryAction}</Box>}
                                {(latestStockLevel || 0) > 0 && isCart && (
                                    <Box
                                        sx={{
                                            alignSelf: 'flex-end'
                                        }}
                                    >
                                        <QuantityPickerSection
                                            quantity={quantity}
                                            setQuantity={setQuantity}
                                            stepQuantity={stepQuantity}
                                            stockLevel={latestStockLevel}
                                            minOrderQuantity={1}
                                            isVisible={true}
                                            onQuantityChange={handleCustomQuantityChange}
                                            product={product}
                                            onCheckStock={handleCheckStock}
                                            disableIncrease={disableQuantityIncrease}
                                        />
                                    </Box>
                                )}
                            </Flex>
                        </Stack>
                        {!isGiftCertificate && shouldShowError && !isOrderConfirmation && (
                            <Stack mt="5px" spacing={1.5} alignItems="flex-start">
                                {product?.isRestricted && !product?.showInStorePickup && (
                                    <Flex flexDirection="row" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'product_item.error.shipment_change',
                                                    defaultMessage: `Item can't be shipped to {shippingState} please remove or change shipping address`
                                                },
                                                {shippingState}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'OUT_OF_STOCK' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage({
                                                id: 'product_item.error.out_of_stock',
                                                defaultMessage:
                                                    'This item is no longer available. Please remove this from the cart.'
                                            })}
                                        </Text>
                                    </Flex>
                                )}
                                {inventoryErrorType === 'QUANTITY_EXCEEDED' && (
                                    <Flex flexDirection="row" align="flex-start" gap="5px">
                                        <Image
                                            src={getAssetUrl('static/img/header/error-symbol.svg')}
                                            alt="Error symbol"
                                            sx={{w: '13px', h: '13px', mt: '2px'}}
                                        />
                                        <Text sx={{...errorMessageStyle, m: 0}}>
                                            {intl.formatMessage(
                                                {
                                                    id: 'product_item.error.quantity_exceeded',
                                                    defaultMessage:
                                                        'The quantity selected is not currently available, please enter a quantity of {stockLevel} or less to proceed with this purchase.'
                                                },
                                                {stockLevel: latestStockLevel}
                                            )}
                                        </Text>
                                    </Flex>
                                )}
                            </Stack>
                        )}
                    </ItemVariantProvider>
                </Box>
            </Box>
        </>
    )
}

ProductItem.propTypes = {
    product: PropTypes.object,
    onItemQuantityChange: PropTypes.func,
    onAddItemToCart: PropTypes.func,
    showLoading: PropTypes.bool,
    isWishlistItem: PropTypes.bool,
    primaryAction: PropTypes.node,
    secondaryActions: PropTypes.node,
    customStyles: PropTypes.object,
    isCart: PropTypes.bool,
    isAccountPage: PropTypes.bool,
    isOrderConfirmation: PropTypes.bool,
    disableQuantityIncrease: PropTypes.bool
}

export default ProductItem
